import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query/react";
import i18n from "i18next";
import { findValueByKeys, reduceObjectByKeys } from "src/utility";
import { RootState } from ".";
import { FlightApiEndpoints } from "./apis/Flights";
import { ClientApiEndpoints } from "./apis/client";
import { TopupApiEndpoints } from "./apis/Topup";
import { PointsExhangeEndpoints } from "./apis/pointsExchange";
import { GiftcardsApiEndpoints } from "./apis/giftcards";
import { isEmbedded } from "src/auth/isEmbedded";
import appConfig from "src/app.config";
import { HotelsApiEndpoints } from "./apis/Hotels";

export const errorsKeys = ["message", "Message", "errorMessage", "ErrorMessage", "Error", "error", "errors", "Errors"];

export const Channel = {
  Web: 1,
  MobileApp: 2,
  Integration: 3,
  POS: 4,
  Sdk: 5,
} as const;

const getChannel = (): number => {
  if (isEmbedded()) return Channel.Integration;
  if ((navigator as any)?.userAgentData?.mobile) return Channel.MobileApp;
  return Channel.Web;
};

export type EndpointType =
  | FlightApiEndpoints
  | HotelsApiEndpoints
  | ClientApiEndpoints
  | TopupApiEndpoints
  | PointsExhangeEndpoints
  | GiftcardsApiEndpoints;

const HeaderConfig: Partial<Record<EndpointType, { ignore?: string[]; attatch?: string[] }>> = {
  getModules: {
    ignore: ["ClientId"],
  },
  getUserDetails: {
    ignore: ["ClientId"],
  },
  initiateOrder:{
    attatch:["Channel"],
  },
  orderItem:{
    attatch:["Channel"],
  },
  checkout:{
    attatch:["Channel"],
  },
  initiateHotelBooking:{
    attatch:["Channel"],
  }
};

export const AuthInterceptor = (headers: Headers, api: { getState: () => unknown; endpoint: string }) => {
  const state = api.getState() as RootState;
  const { auth, client } = state;
  const { token, tenantId, isIntegrated } = auth;
  const clientId = client?.selectedClientId;

  const defaultHeaders: Record<string, string> = {
    AuthenticationType: "1",
    Authorization: `Bearer ${token}`,
    TenantId: tenantId ?? (isIntegrated ? "" : appConfig?.tenantId ?? ""),
    ClientId: clientId ?? "",
    "Accept-Language": `${i18n.language}`,
  };

  const endpoint = api.endpoint as EndpointType;
  const config = HeaderConfig[endpoint];

  if (config?.attatch?.includes("Channel")) {
    headers.set("Channel", String(getChannel()));
  }

  for (const [key, value] of Object.entries(defaultHeaders)) {
    if (!value) continue;
    if (config?.ignore?.includes(key)) continue;
    headers.set(key, value);
  }

  return headers;
};

export interface ApiResponse<T = any> {
  result: T;
  message: string;
  Message?: string;
  statusCode: number;
  errors: any;
  Errors: any;
  statusName: string;
}


export interface ApiValidationErrorResponse {
  data: {
    errors: {
      [key: string]: string[];
    };
  };
}

export const apiErrorMessages: Record<number, string> = {
  400: "The request could not be understood by the server due to malformed syntax",
  401: "You are not authorized to perform this action",
  403: "The request was understood, but it has been refused or access is not allowed",
  404: "The requested resource could not be found",
  500: "Internal server error",
};

export const isApiValidationError = (error: unknown): error is ApiValidationErrorResponse => {
  if (typeof error === "object" && error !== null) {
    const apiError = error as ApiValidationErrorResponse;
    return apiError.data?.errors !== undefined && Object.keys(apiError.data.errors).length > 0;
  }
  return false;
};

export const getApiErrorMessage = (error: FetchBaseQueryError) => {
  if (error.status === "FETCH_ERROR") return "Cannot connect to the server, Check your internet network";
  if (typeof error.status !== "number") return "Unknown error occurred";

  if (isApiValidationError(error)) {
    const apiErrorsList = Object.values(error.data?.errors);
    return apiErrorsList;
  }

  if (typeof error.data === "object" && error.data !== null) {
    const response = error.data as ApiResponse;
    const apiErrorsList = Object.values(reduceObjectByKeys(response, errorsKeys));
    return apiErrorsList ?? findValueByKeys(response, errorsKeys) ?? "Something went wrong, please try again later";
  }

  return apiErrorMessages[error.status] || "Something went wrong, please try again later";
};

export function toFormData(body: any): FormData {
  const bodyFormData = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined) return;
    bodyFormData.append(key, value as string | Blob);
  });
  return bodyFormData;
}

export const ensureItEndsWithSlash = (url: string) => {
  return url.replace(/\/?$/, "/");
};

function base64ToBlob(base64: string, contentType: string = ""): Blob {
  // Split the base64 string into data and media type
  const parts = base64.split(";");
  if (parts.length !== 2) {
    throw new Error("Invalid base64 string");
  }

  // Extract the base64 data
  const match = parts[1].match(/base64,(.*)$/);
  if (!match || match.length !== 2) {
    throw new Error("Invalid base64 string");
  }

  const base64Data = match[1];
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
}

export function objectToFormData(obj: any, form?: FormData, namespace?: string): FormData {
  const formData = form || new FormData();

  for (let property in obj) {
    if (!obj.hasOwnProperty(property) || obj[property] === undefined || obj[property] === null) continue;

    const formKey = namespace ? `${namespace}[${property}]` : property;

    if (typeof obj[property] === "object" && !(obj[property] instanceof File)) {
      objectToFormData(obj[property], formData, formKey);
    } else if (typeof obj[property] === "string" && obj[property].startsWith("data:image")) {
      const contentType = obj[property].split(";")[0].split(":")[1];
      const ext = contentType.split("/")[1];
      formData.append(formKey, base64ToBlob(obj[property], contentType), `filename.${ext}`);
    } else {
      formData.append(formKey, obj[property]);
    }
  }

  return formData;
}

export const objectToQueryParams = (baseUrl: string = "", queryParams?: Record<string, any>) => {
  if (!queryParams || Object.keys(queryParams).length === 0) return baseUrl;
  const queryString = Object.entries(queryParams)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");

  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};
//the arrays are not handled here and should be considered if you wanna pass array in query params , for god knows why?
